/*
 * tg-adult-tabs
 *
 * Tabbed container (What To Expect / FAQs / Reviews) for the adult Creatine PDP.
 *
 * Deliberate implementation notes:
 *
 * 1. Custom element rather than an IIFE. `assets/tg-adult-testimonials.js`
 *    uses a bare IIFE that runs once at parse time, so its tabs go dead after a
 *    Shopify theme-editor section reload. connectedCallback re-runs on every
 *    re-insert, which is what we want.
 *
 * 2. Inactive panels are hidden with [data-inactive] + [inert], never
 *    display:none. Nothing in this section currently depends on that, but the
 *    Judge.me widget lower on the page measures its container width at
 *    hydration, and keeping the pattern consistent avoids a future foot-gun if
 *    a widget is ever moved in here.
 *
 * 3. The FAQ accordion never measures scrollHeight. Height is animated purely
 *    in CSS via grid-template-rows 0fr -> 1fr, so an accordion living inside an
 *    inactive (zero-height) panel still opens to the correct size. The theme's
 *    other accordion (sections/ecom-faq.liquid) does measure scrollHeight and
 *    would break in that situation.
 *
 * 4. All queries are scoped to `this`. There are intentionally no document- or
 *    window-level listeners: sections/ecom-faq.liquid already binds a GLOBAL
 *    document.querySelectorAll(".faq--item") listener, so this file stays out
 *    of the shared namespace entirely.
 */
if (!customElements.get("tg-adult-tabs")) {
  customElements.define(
    "tg-adult-tabs",
    class TgAdultTabs extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === "true") return;
        this.dataset.ready = "true";

        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

        if (!this.tabs.length || !this.panels.length) return;

        this.onTabClick = this.onTabClick.bind(this);
        this.onTabKeydown = this.onTabKeydown.bind(this);

        this.tabs.forEach((tab) => {
          tab.addEventListener("click", this.onTabClick);
          tab.addEventListener("keydown", this.onTabKeydown);
        });

        this.initFaq();
        this.initReviewsCta();
        this.initFaqJumps();

        // Sync to whatever the server rendered as selected. focus:false is
        // essential here: focusing on load would steal focus from the top of
        // the page and scroll-jump the shopper down to this section.
        var initial =
          this.tabs.find(function (t) {
            return t.getAttribute("aria-selected") === "true";
          }) || this.tabs[0];

        this.activate(initial, { focus: false });
      }

      disconnectedCallback() {
        this.tabs.forEach((tab) => {
          tab.removeEventListener("click", this.onTabClick);
          tab.removeEventListener("keydown", this.onTabKeydown);
        });
      }

      onTabClick(event) {
        this.activate(event.currentTarget);
      }

      onTabKeydown(event) {
        var index = this.tabs.indexOf(event.currentTarget);
        if (index === -1) return;

        var next = null;
        var last = this.tabs.length - 1;

        if (event.key === "ArrowRight" || event.key === "ArrowDown") {
          next = index === last ? 0 : index + 1;
        } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
          next = index === 0 ? last : index - 1;
        } else if (event.key === "Home") {
          next = 0;
        } else if (event.key === "End") {
          next = last;
        }

        if (next === null) return;

        event.preventDefault();
        this.activate(this.tabs[next]);
      }

      activate(tab, options) {
        if (!tab) return;

        var targetId = tab.getAttribute("aria-controls");

        this.tabs.forEach(function (item) {
          var isActive = item === tab;
          item.classList.toggle("is-active", isActive);
          item.setAttribute("aria-selected", isActive ? "true" : "false");
          item.tabIndex = isActive ? 0 : -1;
        });

        this.panels.forEach(function (panel) {
          var isActive = panel.id === targetId;
          panel.toggleAttribute("data-inactive", !isActive);
          panel.toggleAttribute("inert", !isActive);
        });

        if (!options || options.focus !== false) {
          tab.focus();
        }
      }

      initFaq() {
        var exclusive = this.dataset.faqExclusive === "true";
        var items = Array.from(this.querySelectorAll("[data-tgat-faq]"));

        items.forEach(function (item) {
          var trigger = item.querySelector("[data-tgat-faq-q]");
          if (!trigger) return;

          trigger.addEventListener("click", function () {
            var willOpen = !item.classList.contains("is-open");

            if (willOpen && exclusive) {
              items.forEach(function (other) {
                if (other === item) return;
                other.classList.remove("is-open");
                var otherTrigger = other.querySelector("[data-tgat-faq-q]");
                if (otherTrigger) {
                  otherTrigger.setAttribute("aria-expanded", "false");
                }
              });
            }

            item.classList.toggle("is-open", willOpen);
            trigger.setAttribute("aria-expanded", willOpen ? "true" : "false");
          });
        });
      }

      /*
       * Height of anything pinned to the top of the viewport. Only counts
       * elements actually at the top (<= 4px) that are plausibly a bar rather
       * than a full-screen overlay (< 200px tall), so a sticky sidebar or an
       * open modal cannot inflate the offset.
       */
      stickyOffset() {
        var max = 0;
        var nodes = document.body.querySelectorAll("*");

        for (var i = 0; i < nodes.length; i++) {
          var pos = getComputedStyle(nodes[i]).position;
          if (pos !== "fixed" && pos !== "sticky") continue;

          var r = nodes[i].getBoundingClientRect();
          if (r.height <= 0 || r.height >= 200) continue;
          if (r.top > 4 || r.bottom <= 0) continue;
          if (r.bottom > max) max = r.bottom;
        }

        return max;
      }

      /*
       * Scroll to another section on the page. scrollIntoView is deliberately
       * not used: it puts the target at viewport top, underneath the sticky
       * header, so the destination heading is hidden. Adding scroll-margin-top
       * to the targets is not an option either, since they belong to other
       * sections.
       */
      jumpTo(selector) {
        if (!selector) return;

        var target = document.querySelector(selector);
        if (!target) return;

        var top =
          target.getBoundingClientRect().top +
          window.pageYOffset -
          this.stickyOffset() -
          16;

        if (top < 0) top = 0;

        var reduce =
          window.matchMedia &&
          window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        window.scrollTo({ top: top, behavior: reduce ? "auto" : "smooth" });

        // Move focus so keyboard and screen-reader users follow the jump. The
        // tabindex is removed again on blur so the section never becomes a
        // lingering tab stop.
        if (!target.hasAttribute("tabindex")) {
          target.setAttribute("tabindex", "-1");
          // Suppressed inline, not in the stylesheet: the target belongs to
          // another section and this file styles nothing outside its own
          // namespace. A focus ring around a whole section reads as a bug.
          var prevOutline = target.style.outline;
          target.style.outline = "none";
          target.addEventListener(
            "blur",
            function () {
              target.removeAttribute("tabindex");
              target.style.outline = prevOutline;
            },
            { once: true },
          );
        }
        target.focus({ preventScroll: true });
      }

      initReviewsCta() {
        var cta = this.querySelector("[data-tgat-reviews-cta]");
        if (!cta) return;

        var self = this;
        cta.addEventListener("click", function (event) {
          event.preventDefault();
          self.jumpTo(cta.getAttribute("data-tgat-reviews-cta"));
        });
      }

      initFaqJumps() {
        var self = this;
        var buttons = Array.from(this.querySelectorAll("[data-tgat-jump]"));

        buttons.forEach(function (btn) {
          btn.addEventListener("click", function (event) {
            event.preventDefault();
            self.jumpTo(btn.getAttribute("data-tgat-jump"));
          });
        });
      }
    },
  );
}
